import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Typography,
  message,
  Tabs,
  Badge,
  Alert,
  Divider,
  Tooltip
} from "antd";
import {
  CarOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  HistoryOutlined,
  BankOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../utils/apiClient";

const { Title, Text } = Typography;
const { Option } = Select;

const TransportPaymentsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [transportSummary, setTransportSummary] = useState([]);
  const [paymentsHistory, setPaymentsHistory] = useState([]);

  // Active Sub-tab in Transport View
  const [subTab, setSubTab] = useState("suppliers");

  // Modals
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [addSupplierModalVisible, setAddSupplierModalVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [ledgerModalVisible, setLedgerModalVisible] = useState(false);

  const [paymentForm] = Form.useForm();
  const [addSupplierForm] = Form.useForm();

  useEffect(() => {
    fetchTransportSummary();
    fetchPaymentsHistory();
  }, []);

  const fetchTransportSummary = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/transport-payments/summary");
      if (res.data?.success) {
        setTransportSummary(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load transport summary:", err);
      message.error("Failed to load transport payment summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsHistory = async () => {
    try {
      const res = await apiClient.get("/transport-payments/payments");
      if (res.data?.success) {
        setPaymentsHistory(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to load transport payments history:", err);
    }
  };

  // Handle Log Payment submit
  const handleLogPayment = async (values) => {
    try {
      const payload = {
        ...values,
        supplierName: selectedSupplier ? selectedSupplier.supplierName : values.supplierName,
        supplierId: selectedSupplier ? selectedSupplier.supplierId : null,
        paymentDate: values.paymentDate ? values.paymentDate.format("YYYY-MM-DD") : new Date()
      };

      await apiClient.post("/transport-payments/payments", payload);
      message.success(`Payment of ₹${values.amount.toLocaleString("en-IN")} logged successfully!`);
      setPaymentModalVisible(false);
      paymentForm.resetFields();
      setSelectedSupplier(null);

      fetchTransportSummary();
      fetchPaymentsHistory();
    } catch (err) {
      console.error("Failed to log transport payment:", err);
      message.error(err.response?.data?.message || "Failed to log payment");
    }
  };

  // Handle Create Transport Supplier Account
  const handleCreateSupplierAccount = async (values) => {
    try {
      await apiClient.post("/transport-payments/suppliers", values);
      message.success(`Transport Supplier "${values.name}" registered successfully with bank details!`);
      setAddSupplierModalVisible(false);
      addSupplierForm.resetFields();
      fetchTransportSummary();
    } catch (err) {
      console.error("Failed to create transport supplier:", err);
      message.error(err.response?.data?.message || "Failed to create transport supplier");
    }
  };

  // Aggregated Stats
  const totalEffectivePayable = transportSummary.reduce((acc, curr) => acc + (curr.totalEffectivePayable || 0), 0);
  const totalPaid = transportSummary.reduce((acc, curr) => acc + (curr.totalPaid || 0), 0);
  const totalOutstanding = totalEffectivePayable - totalPaid;
  const activeSuppliersCount = transportSummary.length;

  const supplierColumns = [
    {
      title: "Transport Supplier",
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{name}</div>
          {record.city && <Tag color="cyan" style={{ fontSize: 10, marginTop: 2 }}>{record.city}</Tag>}
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
            {record.totalTripsCount} Trips ({record.activeTripsCount} Active
            {record.cancelledTripsCount > 0 ? `, ${record.cancelledTripsCount} Cancelled` : ""})
          </div>
          {record.contact?.person && (
            <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>
              <UserOutlined style={{ marginRight: 4 }} />{record.contact.person} {record.contact.phone ? `(${record.contact.phone})` : ""}
            </div>
          )}
        </div>
      )
    },
    {
      title: "Bank & Settlement Details",
      key: "bankDetails",
      render: (_, record) => {
        const b = record.bankDetails || {};
        const hasBank = b.bankName || b.accountNumber || b.upiId;
        if (!hasBank) {
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              No bank details added
            </Text>
          );
        }
        return (
          <div style={{ fontSize: 11, background: "#f8fafc", padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0" }}>
            {b.bankName && <div><strong>Bank:</strong> {b.bankName}</div>}
            {b.accountNumber && <div><strong>A/C:</strong> {b.accountNumber} {b.ifscCode ? `(IFSC: ${b.ifscCode})` : ""}</div>}
            {b.accountHolderName && <div><strong>Name:</strong> {b.accountHolderName}</div>}
            {b.upiId && <div><strong>UPI:</strong> {b.upiId}</div>}
            {b.gstin && <div><strong>GSTIN:</strong> {b.gstin}</div>}
          </div>
        );
      }
    },
    {
      title: "Total Payable (₹)",
      dataIndex: "totalEffectivePayable",
      key: "totalEffectivePayable",
      align: "right",
      render: (val) => <span style={{ fontWeight: 600 }}>₹{(val || 0).toLocaleString("en-IN")}</span>
    },
    {
      title: "Total Paid (₹)",
      dataIndex: "totalPaid",
      key: "totalPaid",
      align: "right",
      render: (val) => <span style={{ color: "#16a34a", fontWeight: 600 }}>₹{(val || 0).toLocaleString("en-IN")}</span>
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstandingBalance",
      key: "outstandingBalance",
      align: "right",
      render: (val) => {
        const isSettled = val <= 0;
        return (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: isSettled ? "#16a34a" : "#dc2626" }}>
              ₹{Math.abs(val || 0).toLocaleString("en-IN")}
            </div>
            <Tag color={isSettled ? "success" : "error"} style={{ marginTop: 2, fontSize: 10 }}>
              {isSettled ? "SETTLED / CLEARED" : "OUTSTANDING PENDING"}
            </Tag>
          </div>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<DollarCircleOutlined />}
            style={{ background: "#059669", borderColor: "#059669" }}
            onClick={() => {
              setSelectedSupplier(record);
              paymentForm.setFieldsValue({
                supplierName: record.supplierName,
                amount: record.outstandingBalance > 0 ? record.outstandingBalance : 0
              });
              setPaymentModalVisible(true);
            }}
          >
            Pay Vendor
          </Button>

          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => {
              setSelectedSupplier(record);
              setLedgerModalVisible(true);
            }}
          >
            Ledger & Trips
          </Button>
        </Space>
      )
    }
  ];

  const paymentHistoryColumns = [
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (d) => dayjs(d).format("DD MMM YYYY")
    },
    {
      title: "Transport Supplier",
      dataIndex: "supplierName",
      key: "supplierName",
      render: (text) => <strong style={{ color: "#1e293b" }}>{text}</strong>
    },
    {
      title: "Amount Paid (₹)",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (val) => <span style={{ color: "#16a34a", fontWeight: 700 }}>₹{(val || 0).toLocaleString("en-IN")}</span>
    },
    {
      title: "Mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
      render: (mode) => {
        const modeMap = {
          bank_transfer: { label: "Bank Transfer (NEFT/RTGS)", color: "blue" },
          upi: { label: "UPI Payment", color: "purple" },
          cheque: { label: "Cheque", color: "orange" },
          cash: { label: "Cash", color: "green" },
          credit_card: { label: "Credit Card", color: "cyan" }
        };
        const m = modeMap[mode] || { label: mode, color: "default" };
        return <Tag color={m.color}>{m.label}</Tag>;
      }
    },
    {
      title: "UTR / Reference No.",
      dataIndex: "utrNumber",
      key: "utrNumber",
      render: (text) => text ? <code style={{ fontSize: 11, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{text}</code> : "-"
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (text) => text || "-"
    }
  ];

  const supplierTripsColumns = [
    {
      title: "Booking Ref",
      dataIndex: "bookingReference",
      key: "bookingReference",
      render: (ref) => <strong>{ref || "BOOKING"}</strong>
    },
    {
      title: "Trip Title & Customer",
      key: "title",
      render: (_, r) => (
        <div>
          <div>{r.title}</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>Pax: {r.customerName}</div>
        </div>
      )
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => d ? dayjs(d).format("DD MMM YYYY") : "-"
    },
    {
      title: "Vehicle & Driver",
      key: "vehicle",
      render: (_, r) => (
        <div style={{ fontSize: 11 }}>
          <div><Tag color="geekblue" style={{ fontSize: 10 }}>{r.vehicleType}</Tag></div>
          {r.driverName && <div style={{ marginTop: 2 }}>{r.driverName} {r.driverPhone ? `(${r.driverPhone})` : ""}</div>}
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st) => (
        <Tag color={st === "CANCELLED" ? "error" : "success"}>
          {st}
        </Tag>
      )
    },
    {
      title: "Payable Cost (₹)",
      dataIndex: "effectivePayable",
      key: "effectivePayable",
      align: "right",
      render: (val) => <strong>₹{(val || 0).toLocaleString("en-IN")}</strong>
    }
  ];

  return (
    <div style={{ padding: "12px 0" }}>
      {/* Metrics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, borderLeft: "4px solid #4f46e5" }} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ fontSize: 13, color: "#64748b" }}>Total Transport Cost (Payable)</span>}
              value={totalEffectivePayable}
              precision={0}
              prefix="₹"
              valueStyle={{ color: "#1e293b", fontWeight: 700 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, borderLeft: "4px solid #16a34a" }} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ fontSize: 13, color: "#64748b" }}>Total Paid to Vendors</span>}
              value={totalPaid}
              precision={0}
              prefix="₹"
              valueStyle={{ color: "#16a34a", fontWeight: 700 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, borderLeft: "4px solid #dc2626" }} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ fontSize: 13, color: "#64748b" }}>Outstanding Vendor Balance</span>}
              value={totalOutstanding}
              precision={0}
              prefix="₹"
              valueStyle={{ color: totalOutstanding > 0 ? "#dc2626" : "#16a34a", fontWeight: 700 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: 12, borderLeft: "4px solid #0284c7" }} bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ fontSize: 13, color: "#64748b" }}>Active Transport Vendors</span>}
              value={activeSuppliersCount}
              valueStyle={{ color: "#0284c7", fontWeight: 700 }}
              prefix={<CarOutlined style={{ marginRight: 6 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Header & Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>🚖 Transport Suppliers & Vendor Payment Ledger</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Register transport vendors, log disbursement payments, track bank details, and manage outstanding balances</Text>
        </div>

        <Space wrap>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={() => {
              addSupplierForm.resetFields();
              setAddSupplierModalVisible(true);
            }}
            style={{ borderColor: "#0284c7", color: "#0284c7", fontWeight: 600 }}
          >
            Add Transport Supplier Account
          </Button>

          <Button
            type="primary"
            icon={<DollarCircleOutlined />}
            onClick={() => {
              setSelectedSupplier(null);
              paymentForm.resetFields();
              setPaymentModalVisible(true);
            }}
            style={{ background: "#059669", borderColor: "#059669" }}
          >
            Log Transport Payment
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 12 }}>
        <Tabs
          activeKey={subTab}
          onChange={setSubTab}
          items={[
            {
              key: "suppliers",
              label: (
                <span>
                  <CarOutlined style={{ marginRight: 6 }} />
                  Vendor Outstanding Balances & Accounts ({transportSummary.length})
                </span>
              ),
              children: (
                <Table
                  columns={supplierColumns}
                  dataSource={transportSummary}
                  rowKey="supplierName"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 800 }}
                />
              )
            },
            {
              key: "history",
              label: (
                <span>
                  <HistoryOutlined style={{ marginRight: 6 }} />
                  Disbursement Payment Log ({paymentsHistory.length})
                </span>
              ),
              children: (
                <Table
                  columns={paymentHistoryColumns}
                  dataSource={paymentsHistory}
                  rowKey="_id"
                  loading={loading}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 800 }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* MODAL 1: Log Transport Payment */}
      <Modal
        title="Log Payment to Transport Vendor"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={550}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Form form={paymentForm} layout="vertical" onFinish={handleLogPayment}>
          <Form.Item name="supplierName" label="Transport Supplier Name" rules={[{ required: true, message: "Supplier name is required" }]}>
            <Input placeholder="Select or type supplier name..." disabled={!!selectedSupplier} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="amount" label="Amount Paid (₹)" rules={[{ required: true, message: "Amount is required" }]}>
                <InputNumber style={{ width: "100%" }} min={1} placeholder="10000" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="paymentDate" label="Payment Date" initialValue={dayjs()}>
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="paymentMode" label="Payment Mode" initialValue="bank_transfer">
                <Select>
                  <Option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</Option>
                  <Option value="upi">UPI Payment</Option>
                  <Option value="cheque">Cheque</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="credit_card">Credit Card</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="utrNumber" label="UTR / Transaction Ref No.">
                <Input placeholder="UTR12345678" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="bookingReference" label="Booking Ref / Trip ID (Optional)">
            <Input placeholder="e.g. TRV-2026-901" />
          </Form.Item>

          <Form.Item name="notes" label="Notes / Remarks">
            <Input.TextArea rows={2} placeholder="Advance paid for Innova Crysta booking in Manali" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => setPaymentModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#059669", borderColor: "#059669" }}>
              Submit Vendor Payment
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL 2: View Supplier Ledger & Assigned Trips */}
      <Modal
        title={`Trips & Service Details - ${selectedSupplier?.supplierName || "Supplier"}`}
        open={ledgerModalVisible}
        onCancel={() => setLedgerModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLedgerModalVisible(false)}>
            Close
          </Button>
        ]}
        width={850}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Table
          columns={supplierTripsColumns}
          dataSource={selectedSupplier?.trips || []}
          rowKey={(r, i) => `${r.bookingId}_${i}`}
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
        />
      </Modal>

      {/* MODAL 3: Add New Transport Supplier Account */}
      <Modal
        title="Register New Transport Supplier & Bank Details"
        open={addSupplierModalVisible}
        onCancel={() => setAddSupplierModalVisible(false)}
        footer={null}
        width={650}
        style={{ maxWidth: "95vw" }}
        destroyOnClose
      >
        <Form form={addSupplierForm} layout="vertical" onFinish={handleCreateSupplierAccount}>
          <Alert
            message="Transport Supplier / Fleet Owner Information"
            description="Add vendor contact info and bank account details for seamless disbursement of transport payments."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item name="name" label="Supplier / Agency Name" rules={[{ required: true, message: "Supplier name is required" }]}>
                <Input placeholder="e.g. Royal Himachal Travels / Kerala Cab Services" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item name="vehicleTypes" label="Vehicle Fleet Offered">
                <Input placeholder="Innova, Tempo Traveller, Sedan" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="city" label="City" rules={[{ required: true, message: "City is required" }]}>
                <Input placeholder="e.g. Manali / Kochi / Jaipur" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="state" label="State">
                <Input placeholder="e.g. Himachal Pradesh" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>Contact Person</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="contactPerson" label="Contact Person">
                <Input placeholder="Manager / Owner Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="phone" label="Phone / WhatsApp">
                <Input placeholder="+91 9876543210" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="email" label="Email">
                <Input placeholder="cabs@agency.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>Bank Account & Settlement Details</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="bankName" label="Bank Name">
                <Input placeholder="e.g. State Bank of India / HDFC Bank" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="accountHolderName" label="Account Beneficiary Name">
                <Input placeholder="e.g. Royal Travels Pvt Ltd" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="accountNumber" label="Account Number">
                <Input placeholder="620012345678" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="ifscCode" label="IFSC Code">
                <Input placeholder="SBIN0001234" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="upiId" label="UPI ID (VPA)">
                <Input placeholder="royaltravels@sbi" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="gstin" label="GSTIN Number">
                <Input placeholder="02AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
            <Button onClick={() => setAddSupplierModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" style={{ background: "#0284c7", borderColor: "#0284c7" }}>
              Save Transport Supplier Account
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default TransportPaymentsManagement;
